/* eslint-disable */
import { useState, useEffect, useCallback } from "react";
import { doc, updateDoc, collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import Toast, { useToast } from "./Toast";
import { COLOR, FONT, RADIUS } from "../utils/theme";
import { Icon } from "../utils/icons";
import useWindowWidth from "../utils/useWindowWidth";
import { takeSnapshot } from "../utils/snapshots";

// ⚠️ COLOR.inkMid (#8B7355) — URL-encoded as %238B7355 in SVG data URL.
// Cannot reference JS token inside CSS string. Update manually if inkMid changes.
const SELECT_ARROW = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238B7355' stroke-width='1.5' fill='none'/%3E%3C/svg%3E\")";

const labelStyle = {
  fontSize: 11, color: COLOR.inkMid, fontFamily: FONT.mono,
  letterSpacing: "0.5px", textTransform: "uppercase",
  marginBottom: 6, display: "block",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`,
  borderRadius: RADIUS.sm, padding: "10px 12px",
  color: COLOR.inkDark, fontSize: 15,
  fontFamily: FONT.serif,
  outline: "none", transition: "border-color 0.15s",
};
const selectStyle = {
  ...inputStyle, cursor: "pointer", appearance: "none",
  backgroundImage: SELECT_ARROW, backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center", paddingRight: 32,
};
const focus = e => e.target.style.borderColor = COLOR.mauve;
const blur  = e => e.target.style.borderColor = COLOR.borderWarm;

const TRANSIT_OPTIONS = [
  { value: "", label: "None" },
  { value: "plane", label: "Plane" },
  { value: "train", label: "Train" },
  { value: "bus", label: "Bus" },
  { value: "ferry", label: "Ferry" },
  { value: "drive", label: "Drive" },
];
const BUDGET_OPTIONS = [
  { value: "", label: "Trip Default" },
  { value: "budget", label: "Budget" },
  { value: "moderate", label: "Moderate" },
  { value: "splurge", label: "Splurge" },
];
const PACE_OPTIONS = [
  { value: "", label: "No Preference" },
  { value: "relaxed", label: "Relaxed" },
  { value: "moderate", label: "Moderate" },
  { value: "packed", label: "Packed" },
];

const LS_KEY = "wandero_dayeditpanel_width";

export default function DayEditPanel({ day, tripId, onClose, onSave }) {
  const [closing, setClosing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, showToast] = useToast();

  const { width, isDesktop } = useWindowWidth();

  // ── Desktop resize state ────────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? Number(saved) : null;
  });

  useEffect(() => {
    if (panelWidth === null) {
      localStorage.removeItem(LS_KEY);
    } else {
      localStorage.setItem(LS_KEY, panelWidth);
    }
  }, [panelWidth]);

  const resolvedWidth = isDesktop
    ? (panelWidth ?? (width - 80))
    : "100%";

  const handleResizeDragStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth ?? (width - 80);

    const onMove = (ev) => {
      const delta = startX - ev.clientX;
      const newWidth = Math.min(
        width - 80,
        Math.max(width * 0.40, startWidth + delta)
      );
      setPanelWidth(newWidth);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleResizeDoubleClick = () => setPanelWidth(null);

  // ── Form state ──────────────────────────────────────────────────
  const initForm = useCallback((d) => ({
    city:           d.city           || "",
    theme:          d.theme          || "",
    tip:            d.tip            || "",
    notes:          d.notes          || "",
    transitMode:    d.transitMode    || "",
    budgetOverride: d.budgetOverride || "",
    pace:           d.pace           || "",
  }), []);

  const [form, setForm] = useState(() => initForm(day));
  const [initial, setInitial] = useState(() => initForm(day));

  // Re-sync if day prop changes (e.g. user picks a different day while panel is open)
  useEffect(() => {
    const next = initForm(day);
    setForm(next);
    setInitial(next);
  }, [day.id, initForm]); // eslint-disable-line

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const isDirty = Object.keys(form).some(k => form[k] !== initial[k]);

  // ── Dismiss ─────────────────────────────────────────────────────
  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 260);
  }, [onClose]);

  // ── Save ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving || !isDirty) return;
    setSaving(true);
    try {
      const changes = {};
      Object.keys(form).forEach(k => {
        if (form[k] !== initial[k]) {
          // Convert empty string → null for select fields
          changes[k] = form[k] === "" ? null : form[k];
        }
      });
      // Strip undefined values
      Object.keys(changes).forEach(k => {
        if (changes[k] === undefined) delete changes[k];
      });
      takeSnapshot(db, tripId, `Day edited: ${day.city}`, [], []).catch(console.error);
      await updateDoc(doc(db, "days", day.id), changes);

      // Sync trip-level cities array if city changed
      if (changes.city !== undefined) {
        try {
          const daysSnap = await getDocs(
            query(collection(db, "days"), where("ownerUid", "==", auth.currentUser.uid), where("tripId", "==", tripId), orderBy("order"))
          );
          const seen = new Set();
          const updatedCities = [];
          daysSnap.docs.forEach(d => {
            const c = d.id === day.id ? changes.city : d.data().city;
            if (c && !seen.has(c)) {
              seen.add(c);
              updatedCities.push(c);
            }
          });
          await updateDoc(doc(db, "trips", tripId), { cities: updatedCities });
        } catch (e) {
          console.warn("[DayEditPanel] Failed to sync trip cities:", e);
        }
      }

      onSave?.(changes);
      showToast("Day updated", "success");
      setTimeout(() => { setClosing(true); setTimeout(onClose, 260); }, 600);
    } catch (e) {
      setSaving(false);
      showToast("Save failed — please try again", "error");
    }
  };

  return (
    <>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
      <div
        onClick={dismiss}
        style={{
          position: "fixed", inset: 0, zIndex: 300,
          display: "flex",
          alignItems: "stretch",
          justifyContent: isDesktop ? "flex-end" : "center",
          flexDirection: isDesktop ? "row" : "column",
          background: closing ? `${COLOR.inkDark}00` : `${COLOR.inkDark}80`,
          transition: "background 0.26s",
        }}
      >
        <div
          style={{ position: "relative", display: "contents" }}
          onClick={e => e.stopPropagation()}
        >
          <div
            style={{
              position: "relative",
              background: COLOR.blush,
              borderRadius: isDesktop ? 0 : "24px 24px 0 0",
              width: isDesktop ? resolvedWidth : "100%",
              maxWidth: isDesktop ? resolvedWidth : "100%",
              height: isDesktop ? "100vh" : undefined,
              maxHeight: isDesktop ? "100vh" : "100dvh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              paddingBottom: isDesktop ? 24 : "max(16px, env(safe-area-inset-bottom))",
              animation: `${closing ? (isDesktop ? "slideOutRight" : "slideDown") : (isDesktop ? "slideInRight" : "slideUp")} 0.26s cubic-bezier(0.32,0.72,0,1) forwards`,
              boxShadow: isDesktop ? `0 0 60px ${COLOR.inkDark}40` : "none",
            }}
          >
            {/* Desktop resize handle */}
            {isDesktop && (
              <div
                onMouseDown={handleResizeDragStart}
                onDoubleClick={handleResizeDoubleClick}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  cursor: "ew-resize",
                  background: "transparent",
                  zIndex: 10,
                }}
              />
            )}
            {/* Mobile drag handle */}
            {!isDesktop && (
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: COLOR.borderWarm }} />
              </div>
            )}

            {/* Header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: isDesktop ? "18px 20px 14px" : "10px 20px 14px",
              borderBottom: `1px solid ${COLOR.borderWarm}`,
            }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: COLOR.inkDark, fontFamily: FONT.serif }}>
                Edit Day
              </div>
              <button
                onClick={dismiss}
                style={{
                  background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
                  borderRadius: 10, width: 40, height: 40, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
              >
                <Icon name="close" size={16} color={COLOR.inkMid} />
              </button>
            </div>

            {/* Helper text */}
            <div style={{
              padding: "14px 20px 0",
              fontSize: 12, fontFamily: FONT.mono, color: COLOR.inkMid,
              marginBottom: 16,
            }}>
              These fields guide AI when rewriting activities for this day.
            </div>

            {/* Form */}
            <div style={{ padding: "0 20px" }}>

              {/* City */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>CITY</label>
                <input
                  value={form.city}
                  onChange={set("city")}
                  onFocus={focus} onBlur={blur}
                  placeholder="e.g. Stockholm"
                  style={inputStyle}
                />
              </div>

              {/* Theme */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>THEME</label>
                <input
                  value={form.theme}
                  onChange={set("theme")}
                  onFocus={focus} onBlur={blur}
                  placeholder="e.g. Historic Old Town & fjord views"
                  style={inputStyle}
                />
              </div>

              {/* Tip */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>TIP</label>
                <textarea
                  value={form.tip}
                  onChange={set("tip")}
                  onFocus={focus} onBlur={blur}
                  rows={3}
                  placeholder="e.g. Book the 9am ferry to beat crowds"
                  style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
                />
              </div>

              {/* Notes */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>NOTES</label>
                <textarea
                  value={form.notes}
                  onChange={set("notes")}
                  onFocus={focus} onBlur={blur}
                  rows={4}
                  placeholder="Your personal notes for this day"
                  style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
                />
              </div>

              {/* Transit Mode */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>TRANSIT MODE</label>
                <select
                  value={form.transitMode}
                  onChange={set("transitMode")}
                  onFocus={focus} onBlur={blur}
                  style={selectStyle}
                >
                  {TRANSIT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Budget Override */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>BUDGET OVERRIDE</label>
                <select
                  value={form.budgetOverride}
                  onChange={set("budgetOverride")}
                  onFocus={focus} onBlur={blur}
                  style={selectStyle}
                >
                  {BUDGET_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Pace */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>PACE</label>
                <select
                  value={form.pace}
                  onChange={set("pace")}
                  onFocus={focus} onBlur={blur}
                  style={selectStyle}
                >
                  {PACE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

            </div>

            {/* Save button */}
            <div style={{
              padding: "12px 20px",
              display: "flex",
              justifyContent: isDesktop ? "flex-end" : "stretch",
            }}>
              <button
                onClick={handleSave}
                disabled={!isDirty || saving}
                style={{
                  background: isDirty && !saving ? COLOR.mauve : `${COLOR.mauve}66`,
                  color: COLOR.white,
                  border: "none", borderRadius: RADIUS.md,
                  fontFamily: FONT.mono, fontSize: 13,
                  textTransform: "uppercase", letterSpacing: "0.5px",
                  padding: "14px 28px", minHeight: 48,
                  cursor: isDirty && !saving ? "pointer" : "default",
                  transition: "background 0.15s",
                  width: isDesktop ? "auto" : "100%",
                }}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
